import React, { useState } from "react";
import moment from "moment/moment";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/material/Pagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { serverFormat } from "../../../utils/dates";
import InternalActions from "./InternalActions";
import InternalRowMobile from "./InternalRowMobile";
import OrderStatusChip from "../orders/OrderStatusChip";
import MovewareJobIdCell from "src/components/integrations/moveware/MovewareJobIdCell";

export default function InternalTable({
  list,
  refetch,
  page,
  totalPages,
  totalCount,
  onPageChange,
  showBillingCharge = false,
  headerAction = null,
  showMovewareDetails = false,
}) {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const handleRowClick = itemId => {
    setSelectedRowId(itemId);
  };

  const billingChargeTotal = item =>
    item.billingCharges?.grand_total ??
    item.billingCharges?.grandTotal ??
    item.internalWorkOrderCharges?.total;

  const formatBillingCharge = item => {
    const total = billingChargeTotal(item);

    if (total == null || total === "") return "-";

    return Number(total).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  const renderBillingChargeColumn =
    showBillingCharge && list.some(item => Number(billingChargeTotal(item) || 0) !== 0);

  if (!list || list.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body1" color="textSecondary">
          No Internal Orders Found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
        {onPageChange && (
          <Typography color="textSecondary" variant="body2">
            {totalCount} Orders Found. Page {page} of {totalPages}
          </Typography>
        )}
        {headerAction}
      </Box>

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {list.map(item => (
            <InternalRowMobile
              key={`mobile-${item.id}`}
              item={item}
              refetch={refetch}
              showBillingCharge={renderBillingChargeColumn}
              showMovewareDetails={showMovewareDetails}
            />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-root": {
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">Order #</TableCell>
              <TableCell align="center">Submitted On</TableCell>
              <TableCell align="center" sx={{ width: "25%", whiteSpace: "nowrap" }}>
                Submitted By
              </TableCell>
              {showMovewareDetails && <TableCell align="center">Moveware Details</TableCell>}
              <TableCell align="center">Complete By Date</TableCell>
              {renderBillingChargeColumn && <TableCell align="center">Billing Charge</TableCell>}
              <TableCell align="center" sx={{ width: 64, px: 0.5, whiteSpace: "nowrap" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(item => (
              <TableRow
                key={item.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                hover
                selected={selectedRowId === item.id}
                onClick={() => handleRowClick(item.id)}
              >
                <TableCell component="th" scope="row" align="center">
                  {item.revisionNumber > 0
                    ? `${item.internalWorkOrderNumber}.R${item.revisionNumber}`
                    : item.internalWorkOrderNumber}
                </TableCell>
                <TableCell align="center">
                  {moment(item.createdAt, serverFormat).format("lll")}
                </TableCell>
                <TableCell align="center" sx={{ width: "25%", whiteSpace: "nowrap" }}>
                  {item.user?.email}
                </TableCell>
                {showMovewareDetails && (
                  <TableCell align="center">
                    <MovewareJobIdCell item={item} />
                  </TableCell>
                )}
                <TableCell align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.75}>
                    <span>
                      {item.deadlineDate ? moment(item.deadlineDate, serverFormat).format("ll") : "-"}
                    </span>
                    <OrderStatusChip vaStatus={item.vaStatus} orderType="internalWorkOrder" />
                  </Box>
                </TableCell>
                {renderBillingChargeColumn && (
                  <TableCell align="center">{formatBillingCharge(item)}</TableCell>
                )}
                <TableCell align="right" sx={{ width: 64, px: 0.5 }}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <InternalActions item={item} refetch={refetch} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {onPageChange && (
        <Box mb={2} mt={2} display="flex" justifyContent="center">
          {totalPages > 1 && (
            <Pagination color="primary" count={totalPages} page={page} onChange={onPageChange} />
          )}
        </Box>
      )}
    </>
  );
}
